<script lang="ts" setup>
import GridHeader from "@/components/grids/GridHeader.vue";
import type { TravelOrderOrderInfo } from "@/api/trips/dto/trip.dto";
import { ActiveRouteStatuses, RouteStatus } from "@/constants/trip";
import { ref } from "vue";
import ExpandButton from "@/components/buttons/ExpandButton.vue";
import OrderRefs from "@/components/order/OrderRefs.vue";
import TruckRouteStopInfo from "@/views/trucks/components/TruckRouteStopInfo.vue";
import { useFilters } from "@/composables";
import { useTrip } from "@/composables/usetTrip";
import type { CustomerDto } from "@/api/company/dto/customer.dto";
import TruckRouteBookedBy from "@/views/trucks/components/TruckRouteBookedBy.vue";
import type { TruckRoute } from "@/api/trucks/dto/truck.dto";

const { miles } = useFilters();
const { getLoadedMiles } = useTrip();

defineModel<TruckRoute | null>("route", { default: null });
defineModel<boolean>("statusDialog", { default: false });

const props = defineProps<{
  routes: TruckRoute[];
  customers: CustomerDto[];
}>();

const emits = defineEmits<{
  (e: "update:route", value: TruckRoute): void;
  (e: "update:statusDialog", value: boolean): void;
}>();

const openedPanels = ref<number[]>([]);

function getRouteClass(route: TruckRoute) {
  if (route.status === RouteStatus.inProgress || route.status === RouteStatus.notStarted) {
    return "bg-green-lighten-5";
  } else {
    return "bg-white";
  }
}

function getFirstPick(route: TruckRoute) {
  return route.routeStops[0];
}

function getLastDelivery(route: TruckRoute) {
  return route.routeStops[route.routeStops.length - 1];
}

function togglePanel(id: number) {
  if (openedPanels.value.includes(id)) {
    openedPanels.value = openedPanels.value.filter((openedId) => openedId !== id);
  } else {
    openedPanels.value.push(id);
  }
}

function getFutureStatus(status: RouteStatus) {
  return status === RouteStatus.planned ? "active" : "planned";
}

function openHandleStatusDialog(route: TruckRoute) {
  emits("update:route", route);
  emits("update:statusDialog", true);
}

function checkIsSetStatusDisabled(route: TruckRoute) {
  return Boolean(route.routeStops[0].checkInTime && ActiveRouteStatuses.includes(route.status));
}

function getOrder(route: TruckRoute): TravelOrderOrderInfo {
  return route.travelOrders[0].order;
}

function getCustomer(route: TruckRoute): CustomerDto | undefined {
  return props.customers.find((customer) => customer.id === route.travelOrders[0].order.billToCustomerId);
}
</script>

<template>
  <div class="truck-routes">
    <grid-header>
      <div>Order</div>
      <div>Ref number / Bill to company</div>
      <div>First pick up</div>
      <div>Last delivery</div>
      <div>Booked by</div>
      <div>Empty | Loaded miles</div>
      <div>Actions</div>
    </grid-header>

    <template v-if="routes.length">
      <v-expansion-panels v-model="openedPanels">
        <v-expansion-panel
          v-for="route of routes"
          :key="route.id"
          :class="getRouteClass(route)"
          class="truck-routes__item"
          hide-actions
          readonly
        >
          <v-expansion-panel-title class="truck-routes__content font-size-14">
            <div class="px-4 py-3">
              <div class="d-flex align-center">
                <expand-button
                  v-if="route.id"
                  :expanded="openedPanels.includes(route.id)"
                  class="text-primary mr-2"
                  size="xsmall"
                  @click="togglePanel(route.id)"
                ></expand-button>
                <router-link
                  :to="{ name: 'order-view', params: { id: getOrder(route).id } }"
                  class="text-decoration-none text-primary font-size-14"
                  data-qa="order-number"
                  target="_blank"
                >
                  {{ getOrder(route).number }}
                </router-link>
              </div>
            </div>
            <div class="px-4 py-3">
              <order-refs :refs="getOrder(route).refNumbers" ref-class="text-grey-darken-2 mb-2"></order-refs>
              <router-link
                v-if="getCustomer(route)"
                :to="{ name: 'customer-edit', params: { id: getCustomer(route)?.id } }"
                class="text-decoration-none text-primary font-size-14 mt-2"
                data-qa="order-number"
                target="_blank"
              >
                {{ getCustomer(route)?.name }}
              </router-link>
            </div>
            <div class="px-4 py-3">
              <truck-route-stop-info :route-stop="getFirstPick(route)" />
            </div>
            <div class="px-4 py-3">
              <truck-route-stop-info :route-stop="getLastDelivery(route)" />
            </div>
            <div class="px-4 py-3">
              <truck-route-booked-by :route="route" />
            </div>
            <div class="px-4 py-3">
              <span class="text-grey-darken-3"
                >{{ miles(route.emptyMiles) || 0 }} miles | {{ miles(getLoadedMiles(route.distances)) }} miles</span
              >
            </div>
            <div class="px-4 py-3">
              <div class="d-flex align-center">
                <v-btn
                  :to="{ name: 'trip-view', params: { id: route.tripId } }"
                  class="font-size-11 mr-6 font-weight-600 text-uppercase"
                  color="success"
                  size="small"
                  target="_blank"
                  variant="flat"
                >
                  View trip
                </v-btn>

                <v-menu>
                  <template #activator="{ props: menuProps }">
                    <v-btn icon="mdi-dots-horizontal" size="xsmall" v-bind="menuProps" variant="text"></v-btn>
                  </template>

                  <v-list>
                    <v-tooltip :disabled="!checkIsSetStatusDisabled(route)" location="top">
                      <template #activator="{ props: tooltipProps }">
                        <div v-bind="tooltipProps">
                          <v-list-item
                            :disabled="checkIsSetStatusDisabled(route)"
                            @click="openHandleStatusDialog(route)"
                          >
                            <v-list-item-title>Set as {{ getFutureStatus(route.status) }}</v-list-item-title>
                          </v-list-item>
                        </div>
                      </template>

                      Route already has progress and can’t be made planned
                    </v-tooltip>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </v-expansion-panel-title>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <div v-else class="truck-routes__no-routes bg-white d-flex align-center justify-center w-100 h-100 py-10">
      <slot name="noRoutes"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.truck-routes {
  display: grid;
  grid-template-columns: repeat(7, auto);

  &__item {
    background-color: $white;
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    box-shadow: none;
  }

  &__no-routes {
    grid-column: 1/-1;
  }

  &__content {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    padding: 0;
    align-items: flex-start;
    user-select: text;
  }

  .v-expansion-panels {
    :deep {
      .v-expansion-panel__shadow {
        display: none;
      }

      .v-expansion-panel-title__overlay {
        display: none;
      }
    }
  }
}
</style>
